// Speaker list of a meeting: coloured chips, tap to rename inline.
import { useEffect, useRef, useState } from 'react';
import type { Speaker } from '../../../shared/types';
import type { ViewDocument } from '../lib/api';
import { defaultSpeakerName, speakerColor } from '../lib/format';

function keyNumber(key: string): number {
  const n = /^S(\d+)$/.exec(key)?.[1];
  return n ? Number(n) : Number.MAX_SAFE_INTEGER;
}

/**
 * Speakers stored on the document plus keys that only appear in segments (e.g. assigned offline),
 * ordered S1, S2, ...
 */
export function mergeSpeakers(doc: Pick<ViewDocument, 'speakers' | 'clips'>): Speaker[] {
  const byKey = new Map<string, Speaker>();
  for (const s of doc.speakers) byKey.set(s.key, { key: s.key, name: s.name || defaultSpeakerName(s.key) });
  for (const clip of doc.clips) {
    for (const seg of clip.segments) {
      if (seg.speaker && !byKey.has(seg.speaker)) byKey.set(seg.speaker, { key: seg.speaker, name: defaultSpeakerName(seg.speaker) });
    }
  }
  return [...byKey.values()].sort((a, b) => keyNumber(a.key) - keyNumber(b.key) || a.key.localeCompare(b.key));
}

/** First unused "S<n>" key. */
export function nextSpeakerKey(speakers: readonly Speaker[]): string {
  const used = new Set(speakers.map((s) => s.key));
  let n = 1;
  while (used.has(`S${n}`)) n++;
  return `S${n}`;
}

/** Speakers that actually occur in the transcript (unused entries are hidden). */
export function speakersInUse(doc: Pick<ViewDocument, 'speakers' | 'clips'>): Speaker[] {
  const used = new Set<string>();
  for (const clip of doc.clips) for (const seg of clip.segments) if (seg.speaker) used.add(seg.speaker);
  return mergeSpeakers(doc).filter((s) => used.has(s.key));
}

function SpeakerChip({ speaker, onRename }: { speaker: Speaker; onRename: (key: string, name: string) => void }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(speaker.name);
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelled = useRef(false);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
      inputRef.current?.select();
    }
  }, [editing]);

  const style = { ['--speaker' as string]: speakerColor(speaker.key) };

  if (editing) {
    const finish = () => {
      setEditing(false);
      if (cancelled.current) {
        cancelled.current = false;
        return;
      }
      const name = draft.trim();
      if (name && name !== speaker.name) onRename(speaker.key, name);
    };
    return (
      <span className="speaker-chip speaker-chip--editing" style={style}>
        <span className="speaker-dot" aria-hidden="true" />
        <input
          ref={inputRef}
          className="speaker-chip__input"
          value={draft}
          maxLength={80}
          aria-label={`Ime za ${speaker.name}`}
          enterKeyHint="done"
          onChange={(e) => setDraft(e.target.value)}
          onBlur={finish}
          onKeyDown={(e) => {
            if (e.key === 'Enter') e.currentTarget.blur();
            if (e.key === 'Escape') {
              cancelled.current = true;
              e.currentTarget.blur();
            }
          }}
        />
      </span>
    );
  }

  return (
    <button
      type="button"
      className="speaker-chip"
      style={style}
      onClick={() => {
        setDraft(speaker.name);
        setEditing(true);
      }}
      aria-label={`${speaker.name} – preimenuj`}
      title="Preimenuj govorca"
    >
      <span className="speaker-dot" aria-hidden="true" />
      <span>{speaker.name}</span>
    </button>
  );
}

export function SpeakerChips({ speakers, onRename }: { speakers: Speaker[]; onRename: (key: string, name: string) => void }) {
  if (speakers.length === 0) return null;
  return (
    <section className="speakers" aria-label="Govorci">
      <h2 className="section-label">Govorci</h2>
      <div className="speakers__list">
        {speakers.map((s) => (
          <SpeakerChip key={s.key} speaker={s} onRename={onRename} />
        ))}
      </div>
      <p className="muted small">Tapnite ime, da ga spremenite (npr. »Janez Novak«).</p>
    </section>
  );
}
